import { toast } from "react-toastify";
import { saveToLocalStorage } from "../utils/localStorage";
import { loginSuccess } from "../slices/authSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { setIsLoggedIn, setRole } from "../slices/app";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VerifyOtpForm = ({ email, formData, onResendOtp }) => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [timer, setTimer] = useState(300);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const inputRefs = useRef([]);
  // format seconds -> mm:ss
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const dispatch = useDispatch();
  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // move to next input if not last and value entered
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const onSubmit = () => {
    const otpValue = otp.join("");
    handleVerifyOtp(otpValue); // send joined OTP
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/v1/auth/verify-otp-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, email: email.toLowerCase(), otp }),
      });
      // const data = await res.json();

      if (res.ok) {
        const data = await res.json();
        dispatch(loginSuccess(data));
        saveToLocalStorage(data);
        localStorage.setItem("name", data?.name);
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("tokenExpiresIn", data.expiresIn);
        localStorage.setItem("role", data.role);
        localStorage.setItem("baseCurrency", data.baseCurrency);
        localStorage.setItem("id", data.id);

        localStorage.setItem("email", data.email);
        localStorage.setItem("isLoggedIn", true);

        if (data.role === process.env.REACT_APP_ADMIN_ROLE_ID) {
          localStorage.setItem("companyName", data.companyInfo?.name);
          localStorage.setItem("companyLogo", data.companyInfo.logo);
          localStorage.setItem("companyId", data.companyInfo._id);
          localStorage.setItem("companyAbout", data.companyInfo.about);
          localStorage.setItem("paymentStatus", data.paymentStatus);
          localStorage.setItem("createdAt", data.createdAt);
          localStorage.setItem("notifyOnExpense", data.notifyOnExpense);
          localStorage.setItem("notifyOnProject", data.notifyOnProject);
        }
        if (data.role === process.env.REACT_APP_USER_ROLE_ID) {
          localStorage.setItem("parentId", data.parentId._id);
          localStorage.setItem("parentName", data.parentId?.name);
          localStorage.setItem("access", data.access);
        }

        dispatch(setIsLoggedIn(true));
        dispatch(setRole(data.role));
        toast.success("Sign Up successful!");
        setTimeout(() => {
          if (data.role === process.env.REACT_APP_SUPERADMIN_ROLE_ID) {
            navigate("/admin/dashboard");
          } else if (data.role === process.env.REACT_APP_USER_ROLE_ID) {
            navigate("/user/dashboard");
          } else {
            navigate("/dashboard");
          }
        }, 1000);
      } else {
        const errorData = await res.json();
        toast.error(`Sign Up failed: ${errorData.error}`);
      }
    } catch (error) {
      toast.error("Error verifying OTP");
    } finally {
      setLoading(false); // stop loading
    }
  };
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleResend = () => {
    onResendOtp();
    setTimer(300); // restart timer (match OTP expiry time in seconds)
  };
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);
  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text").trim();

    // Allow only numbers and max 6 digits
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, 6).split("");

    const newOtp = [...otp];

    digits.forEach((digit, index) => {
      newOtp[index] = digit;
    });

    setOtp(newOtp);

    // Focus last filled input
    const nextIndex = digits.length >= 6 ? 5 : digits.length;
    inputRefs.current[nextIndex]?.focus();
  };
  useEffect(() => {
    if (otp.every((digit) => digit !== "")) {
      onSubmit();
    }
  }, [otp]);
  return (
    <div className="max-w-md w-full bg-white py-6 px-8 rounded-2xl border-4 border-gray-200">
      <h2 className="text-2xl font-bold text-center text-themeColor mb-3">
        Verify OTP
      </h2>
      <p className="text-gray-600 text-center mb-4">
        Enter the 6-digit code sent to{" "}
        <span className="font-semibold">{email}</span>
      </p>

      {/* OTP boxes */}
      <div className="flex flex-col justify-center mb-2">
        <div className="flex justify-center gap-3">
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-themeColor"
            />
          ))}
        </div>
        <span className="self-center px-3 font-semibold text-[#dc2626] mt-2">
          {timer > 0 ? ` ${formatTime(timer)}s` : ""}
        </span>
      </div>
      <button
        onClick={onSubmit}
        className="w-full py-2 px-4 bg-themeColor text-white font-semibold rounded-md shadow-md hover:bg-themeColor2 transition-colors"
      >
        {loading ? "Verifying & Signing up..." : "Verify & Signup"}
      </button>

      <div className="mt-4 text-center">
        <span className="text-gray-600">Didn’t receive the code?</span>
        <button
          className={`ml-1 font-semibold transition duration-200 ${
            timer > 0
              ? "text-gray-400 cursor-not-allowed opacity-50"
              : "text-themeColor"
          }`}
          onClick={handleResend}
          disabled={timer > 0}
        >
          Resend OTP
        </button>
      </div>
    </div>
  );
};

export default VerifyOtpForm;
